import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/Authcontext';
import { getRankDisplay, rankTheme } from '../utils/rankBadge';
import { paymentStatusMeta, membershipBadgeClass, formatEmiWindowDisplay } from '../utils/planStatus';

const RANK_COLORS = {
  bronze:   'text-orange-600 bg-orange-50 border-orange-200',
  silver:   'text-slate-500 bg-slate-50 border-slate-200',
  gold:     'text-amber-600 bg-amber-50 border-amber-200',
  platinum: 'text-cyan-600 bg-cyan-50 border-cyan-200',
  diamond:  'text-violet-600 bg-violet-50 border-violet-200',
};

const RANK_ICONS = {
  bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎', diamond: '✨',
};

const USER_MENU_ITEMS = [
  { to: '/profile', icon: '👤', label: 'Profile' },
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/network', icon: '🌐', label: 'My Network' },
  { to: '/earnings', icon: '💰', label: 'Earnings' },
  { to: '/withdrawal', icon: '🏦', label: 'Withdrawals' },
  { to: '/payments', icon: '💳', label: 'Payments' },
  { to: '/community', icon: '📣', label: 'Community' },
  { to: '/leaderboard', icon: '🏆', label: 'Leaderboard' },
  { to: '/products', icon: '🛍️', label: 'Products' },
  { to: '/about', icon: 'ℹ️', label: 'About' },
  { to: '/rewards', icon: '🎁', label: 'Rewards' },
];

function Navbar() {
  const { user, logout }  = useAuth();
  const navigate          = useNavigate();
  const location          = useLocation();
  const [dropOpen, setDropOpen]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropRef   = useRef(null);
  const drawerRef = useRef(null);

  const isActive = (p) => location.pathname === p;

  useEffect(() => {
    const h = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (drawerRef.current && !drawerRef.current.contains(e.target) &&
          !e.target.closest('[data-drawer-toggle]')) setDrawerOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Hooks must run on every render — never return before useState/useEffect above.
  if (location.pathname.startsWith('/admin-panel')) {
    return null;
  }

  const handleLogout = () => { logout(); setDropOpen(false); setDrawerOpen(false); navigate('/login'); };

  const toggleDropdown = () => {
    setDrawerOpen(false);
    setDropOpen((open) => !open);
  };

  const shortUsername = (name) => {
    if (!name) return '';
    if (name.length <= 4) return name;
    return `${name.slice(0, 4)}..`;
  };

  const navLink = (to, label) => (
    <Link to={to}
      className={`text-sm font-medium px-2 xl:px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
        isActive(to) ? 'text-slate-900 bg-slate-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
      }`}>
      {label}
    </Link>
  );

  const drawerLink = (to, icon, label, sub) => (
    <Link to={to} onClick={() => setDrawerOpen(false)}
      className={`flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors group no-underline ${
        isActive(to) ? 'bg-slate-50' : ''
      }`}>
      <div className="flex items-center gap-3">
        <span className="text-lg w-6 text-center">{icon}</span>
        <div>
          <p className="text-sm font-semibold text-slate-800 group-hover:text-slate-900">{label}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
      <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );

  const rankBadge = getRankDisplay(user);
  const { colorClass: rankColor, icon: rankIcon } = rankTheme(
    rankBadge.themeKey,
    RANK_COLORS,
    RANK_ICONS
  );

  return (
    <>
      <nav className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="w-full px-3 sm:px-4 lg:px-6 h-16 flex items-center gap-1.5 min-w-0">

          {/* Left: hamburger + logo */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button data-drawer-toggle onClick={() => setDrawerOpen(!drawerOpen)}
              className="flex flex-col items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0 gap-1"
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}>
              <span className={`block h-0.5 bg-slate-700 rounded-full transition-all duration-200 ${drawerOpen ? 'rotate-45 translate-y-1.5' : ''}`} style={{width:'16px'}} />
              <span className={`block h-0.5 bg-slate-700 rounded-full transition-all duration-200 ${drawerOpen ? 'opacity-0' : ''}`} style={{width:'16px'}} />
              <span className={`block h-0.5 bg-slate-700 rounded-full transition-all duration-200 ${drawerOpen ? '-rotate-45 -translate-y-1.5' : ''}`} style={{width:'16px'}} />
            </button>

            <Link to="/" className="flex items-center gap-2 no-underline flex-shrink-0">
              <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-black">S</span>
              </div>
              <span className="font-black text-slate-900 text-sm tracking-tight hidden md:block"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>SuccessFlow</span>
            </Link>
          </div>

          {/* Desktop nav — xl+ to prevent overflow on medium screens */}
          <div className="hidden xl:flex flex-1 items-center justify-center min-w-0 overflow-x-auto">
            {navLink('/dashboard',  'Dashboard')}
            {navLink('/network',    'My Network')}
            {navLink('/earnings',   'Earnings')}
            {navLink('/withdrawal', 'Withdrawals')}
            {navLink('/payments',   'Payments')}
            {navLink('/community',  'Community')}
            {navLink('/leaderboard','Leaderboard')}
            {navLink('/products',   'Products')}
            {navLink('/about',      'About')}
            {navLink('/rewards',   'Rewards')}
          </div>

          {/* Right: points + profile */}
          <div className="flex items-center gap-1 flex-shrink-0 ml-auto">

            {/* Points pill */}
            {user && (
              <div
                className="hidden sm:flex items-center gap-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap leading-none"
                title={`${(user.reward_points ?? 0).toLocaleString('en-IN')} reward points`}
              >
                <span className="text-[10px]">⭐</span>
                <span>{(user.reward_points ?? 0).toLocaleString('en-IN')} pts</span>
              </div>
            )}

            {user ? (
              <div className="relative flex items-center" ref={dropRef}>
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className="flex items-center bg-slate-100 hover:bg-slate-200 rounded-full transition-colors p-0.5"
                  aria-label="Open profile menu"
                  aria-expanded={dropOpen}
                  title={user.username}
                >
                  <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {user.username?.[0]?.toUpperCase()}
                  </span>
                  <span className="flex items-center gap-0.5 pl-1 pr-1">
                    <span className="text-xs font-medium text-slate-700 hidden sm:block">
                      {shortUsername(user.username)}
                    </span>
                    <svg
                      className={`w-2.5 h-2.5 text-slate-400 flex-shrink-0 transition-transform ${dropOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>

                {dropOpen && (
                  <div className="absolute right-0 top-11 w-56 bg-white border border-slate-200 rounded-2xl shadow-2xl py-1.5 z-[60]">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-xs font-bold text-slate-900">{user.username}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {rankBadge.show && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${rankColor}`}>
                            {rankIcon} {rankBadge.label}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">⭐ {user.reward_points ?? 0} pts</span>
                      </div>
                    </div>
                    {USER_MENU_ITEMS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setDropOpen(false)}
                        className={`flex items-center gap-2.5 px-4 py-2.5 text-sm no-underline transition-colors ${
                          isActive(item.to)
                            ? 'bg-slate-100 text-slate-900 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span>{item.icon}</span> {item.label}
                      </Link>
                    ))}
                    <div className="h-px bg-slate-100 my-1" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors text-left"
                    >
                      <span>🚪</span> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login"
                className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors no-underline">
                Join Now
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Drawer overlay */}
      {drawerOpen && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setDrawerOpen(false)} />}

      {/* Drawer */}
      <div ref={drawerRef}
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>

        {/* Drawer header */}
        <div className="bg-slate-900 px-5 py-4 flex items-center gap-3 flex-shrink-0">
          {user ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  navigate('/profile');
                }}
                className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-slate-900 text-sm font-black flex-shrink-0 hover:ring-2 hover:ring-amber-200 transition-shadow"
                aria-label="Go to profile"
              >
                {user.username?.[0]?.toUpperCase()}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{user.username}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {rankBadge.show && (
                    <span className="text-amber-400 text-xs font-semibold capitalize">
                      {rankIcon} {rankBadge.label}
                    </span>
                  )}
                  <span className="text-slate-400 text-xs">⭐ {user.reward_points ?? 0} pts</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <span className="text-slate-300 text-lg">👤</span>
              </div>
              <div>
                <p className="text-white text-sm font-bold">Hello, Guest</p>
                <Link to="/login" onClick={() => setDrawerOpen(false)}
                  className="text-amber-400 text-xs font-semibold no-underline hover:text-amber-300">
                  Join SuccessFlow →
                </Link>
              </div>
            </>
          )}
          <button onClick={() => setDrawerOpen(false)} className="ml-auto text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Membership status */}
          {user && (
            <div className="mx-4 mt-4 mb-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Membership</span>
                {(() => {
                  const status = paymentStatusMeta(user);
                  return (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border capitalize ${membershipBadgeClass(status.tone)}`}>
                      {status.tone === 'active' ? '✓ ' : ''}{status.label}
                    </span>
                  );
                })()}
              </div>
              {(() => {
                const status = paymentStatusMeta(user);
                if (status.tone !== 'active') return null;
                const emiWindow = formatEmiWindowDisplay(user.days_remaining, { isActive: true });
                return (
                  <p className="text-xs text-slate-400 mt-1">
                    {emiWindow.value === 'Open'
                      ? 'Payment window: Open'
                      : `${user.days_remaining} days until window opens`}
                  </p>
                );
              })()}
            </div>
          )}

          <div className="border-b border-slate-100">
            <p className="px-5 pt-3 pb-2 text-xs font-bold text-slate-400 uppercase tracking-widest">Main</p>
            {user && drawerLink('/profile',     '👤', 'Profile',      'Account & bank details')}
            {user && drawerLink('/dashboard',   '📊', 'Dashboard',    'Your overview')}
            {user && drawerLink('/network',     '🌐', 'My Network',   'Referral tree')}
            {user && drawerLink('/earnings',    '💰', 'Earnings',     'Points & history')}
            {user && drawerLink('/withdrawal',  '🏦', 'Withdrawals',  'Monthly pay to your bank')}
            {user && drawerLink('/payments',    '💳', 'Payments',     'Plan & history')}
          </div>

          <div className="border-b border-slate-100">
            <p className="px-5 pt-3 pb-2 text-xs font-bold text-slate-400 uppercase tracking-widest">Community</p>
            {drawerLink('/community',   '📣', 'Community',   'News & events')}
            {drawerLink('/leaderboard', '🏆', 'Leaderboard', 'Top earners')}
            {drawerLink('/products',    '🛍️', 'Products',    'Catalog & items')}
            {drawerLink('/about',       'ℹ️', 'About',       'ICONNECT LLP')}
            {drawerLink('/rewards',     '🎁', 'Rewards',     'Leader milestones')}
            {user && drawerLink('/plans', '📋', 'Plans', 'EMI & settlement')}
            {user && drawerLink('/payments', '💳', 'Payments', 'Plan & EMI status')}
          </div>

          {!user && (
            <div className="border-b border-slate-100">
              <p className="px-5 pt-3 pb-2 text-xs font-bold text-slate-400 uppercase tracking-widest">Account</p>
              {drawerLink('/login',          '🔑', 'Sign In',        'Access your account')}
              {drawerLink('/login?mode=signup','✨', 'Join Free',      'Create account')}
              {drawerLink('/products',        '🛍️', 'Products',       'Catalog & items')}
              {drawerLink('/about',           'ℹ️', 'About',          'ICONNECT LLP')}
              {drawerLink('/rewards',         '🎁', 'Rewards',        'Leader milestones')}
            </div>
          )}

          {user?.is_staff && (
            <div className="border-b border-slate-100">
              <p className="px-5 pt-3 pb-2 text-xs font-bold text-slate-400 uppercase tracking-widest">Admin</p>
              {drawerLink('/admin-panel', '⚙️', 'Admin Panel', 'Manage platform')}
            </div>
          )}

          {user && (
            <div className="p-4">
              <button onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-red-100 text-red-500 hover:bg-red-50 transition-colors text-sm font-semibold">
                <span>🚪</span> Sign Out
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex-shrink-0">
          <p className="text-xs text-slate-400 text-center" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            SuccessFlow · Community Platform
          </p>
        </div>
      </div>
    </>
  );
}

export default Navbar;